<template>
  <div>
    <v-img
      :src="headImage.url"
      :lazy-src="headImage.lazyUrl"
      :alt="`Photo by ${headImage.photographerName}`"
      height="600"
      class="mb-6"
      cover
    >
      <div class="overlay" />
      <v-container class="fill-height z-1">
        <v-row class="justify-center">
          <v-col cols="12">
            <div class="text-center">
              <h2
                class="white--text text-h2 font-weight-black text-shadow mb-4"
              >
                Our vision
              </h2>
              <p class="white--text font-weight-bold text-h5 text-shadow mb-0">
                Ensure every photographer can express their full potential, find
                inspiration, improve their skills everywhere, anytime
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <div class="photographer-credit">
        <span class="white--text text-caption">
          Photo by
          <nuxt-link
            :to="headImage.photographerLink"
            class="white--text no-underline font-weight-bold"
          >
            {{ headImage.photographerName }}
          </nuxt-link>
        </span>
      </div>
    </v-img>
    <v-container>
      <v-row class="justify-center mt-0 mb-12">
        <v-col cols="12" xl="8">
          <v-row class="align-center">
            <v-col cols="12" sm="6" md="5">
              <v-img
                src="/images/about-us/mission.jpg"
                alt=""
                aspect-ratio="1.7778"
              ></v-img>
            </v-col>
            <v-col cols="12" sm="6" md="7">
              <v-icon x-large class="mb-3">fal fa-rocket-launch</v-icon>
              <h3 class="text-h4 mb-3">
                Our Mission
              </h3>
              <p class="mb-0 text-body-1">
                Become a reference in digital photography by providing an
                innovative online platform where photographers can share their
                best photos, exchange feedback, find new ideas, and achieve
                their goals. We want to create a place where the eighth art
                plays a leading role in spreading beauty to the world.
              </p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="justify-center">
        <v-col cols="12" xl="8">
          <v-row class="align-center">
            <v-col
              :order="$vuetify.breakpoint.smAndUp ? 1 : null"
              cols="12"
              sm="6"
              md="5"
            >
              <v-img
                src="/images/about-us/who-are-we.jpg"
                alt=""
                aspect-ratio="1.7778"
              ></v-img>
            </v-col>
            <v-col cols="12" sm="6" md="7">
              <v-icon x-large class="mb-3">fal fa-user-friends</v-icon>
              <h3 class="text-h4 mb-3">
                Who are we?
              </h3>
              <p class="mb-0 text-body-1">
                We, Massimiliano and Andrea, are software engineers passionate
                about photography. We put our skills together and developed a
                cutting-edge online digital photography platform:
                <span class="font-weight-bold">100ASA</span>.
              </p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-container
      :class="{
        'light-blue lighten-4': !$vuetify.theme.dark,
        'light-blue darken-3': $vuetify.theme.dark
      }"
      fluid
      class="my-12 pt-12 text-center"
    >
      <v-row justify="center" class="mb-12">
        <v-col cols="12" class="text-center">
          <h2 class="text-h3 mb-3">
            The Team
          </h2>
        </v-col>
        <v-col
          v-for="(member, index) in founders"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          lg="4"
          xl="3"
        >
          <v-avatar size="200" class="mb-3">
            <v-img
              :src="`/images/about-us/crew/${member.photo}`"
              :alt="`${member.fullName} - 100ASA ${member.title}`"
              :position="member.position"
            />
          </v-avatar>
          <h3 class="text-h6 font-weight-bold">
            {{ member.fullName }}
          </h3>
          <h4 class="text-subtitle-1">
            {{ member.title }}
          </h4>
          <h4 v-if="member.subTitle" class="text-subtitle-2">
            {{ member.subTitle }}
          </h4>
        </v-col>
      </v-row>
      <v-row justify="center" class="mb-12">
        <v-col
          v-for="(member, index) in crew"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
        >
          <v-avatar size="200" class="mb-3">
            <v-img
              :src="`/images/about-us/crew/${member.photo}`"
              :alt="`${member.fullName} - 100ASA ${member.title}`"
              :position="member.position"
            />
          </v-avatar>
          <h3 class="text-h6 font-weight-bold">
            {{ member.fullName }}
          </h3>
          <h4 class="text-subtitle-1">
            {{ member.title }}
          </h4>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="mt-2 text-center">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8" xl="6">
          <h3 class="text-h4 mb-3">
            Our story
          </h3>
          <p class="text-body-1 mb-2">
            As photographers, we wanted to find a safe and enjoyable online
            environment to share our images and improve our skills...but no
            matter how hard we tried, we couldn't find one.
          </p>
          <p class="text-body-1 mb-2">
            Over the years, we have signed up to several photography platforms
            but were often disappointed.
          </p>
          <p class="text-body-1 mb-2">
            Some of the reasons for our frustration were an unwelcoming
            environment for new photographers, a plague of fake profiles, a lack
            of rules against mass uploads, and a poor (or non-existent) curation
            process.
          </p>
          <p class="text-body-1 mb-2">
            To change this scenario, we decided to create our own online
            community: 100ASA.
          </p>
          <p class="text-body-1 mb-2">
            We applied our expertise in software engineering and, after months
            of hard work, we developed a reliable, fun, and easy to use online
            environment to help you achieve professional results.
          </p>
          <p class="text-body-1 mb-0">
            We love photography and are excited to continue to build a community
            with people who share our passion.
          </p>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8" xl="5">
          <div class="mb-4 mt-6">
            <v-img
              src="/images/about-us/about-us.jpg"
              alt="The guys behind 100ASA"
              max-width="800"
            />
          </div>
          <div>
            <em>The very first day...</em>
          </div>
        </v-col>
      </v-row>
      <v-row justify="center" class="mt-6">
        <v-col cols="12" md="10" lg="8" xl="6">
          <h3 class="text-h5 mb-3">
            100ASA: The history behind the name
          </h3>
          <p class="text-body-1 mb-2">
            We put a lot of thought when choosing 100ASA as the name of this
            community. The idea was to create a place able to bring back the
            best of the analogic days without losing the advantages offered by
            digital photography.
          </p>
          <p class="text-body-1 mb-2">
            In the analogic days, we had to choose what to photograph carefully.
            We only had up to 36 shots at a time. We spent hours waiting for the
            film to be developed. Mistakes could cost not only money but the
            register of a dear memory.
          </p>
          <p class="text-body-1 mb-2">
            Digital photography ended these limitations but created other
            challenges. Now, it’s too easy to take, print, and share photos. As
            a result, the overall quality of the pictures out there is primarily
            poor.
          </p>
          <p class="text-body-1 mb-0">
            Calling this community 100ASA, one of the most popular films of all
            time, reminds us that quality, not quantity, is what we aim at.
          </p>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  data() {
    return {
      headImage: {
        url:
          'https://storage.100asa.com/staging/5042/tr:n-full_l_v5/09fdc28c-84f0-4a04-93e2-a8f3fa1ae5a3_D_rpJMV8a',
        lazyUrl:
          'https://storage.100asa.com/staging/5042/tr:n-thumb_v5/09fdc28c-84f0-4a04-93e2-a8f3fa1ae5a3_D_rpJMV8a',
        photographerName: 'Stephen Clough',
        photographerLink: '/photographer/vexen'
      },
      founders: [
        {
          photo: 'massimiliano_peluso.jpg',
          fullName: 'Massimiliano Peluso',
          title: 'Founder',
          subTitle: 'Enterprise Solution Architect',
          position: 'top center'
        },
        {
          photo: 'andrea_turri.jpg',
          fullName: 'Andrea Turri',
          title: 'Founder',
          subTitle: 'Technical Leader',
          position: 'top center'
        }
      ],
      crew: [
        {
          photo: 'stefano_porcari.jpg',
          fullName: 'Stefano Porcari',
          title: 'Marketing Manager',
          position: 'top center'
        },
        {
          photo: 'milvia_vincenzini.jpg',
          fullName: 'Milvia Vincenzini',
          title: 'Art Director',
          position: 'top center'
        },
        {
          photo: 'ali_khataw.jpg',
          fullName: 'Ali Khataw',
          title: 'Head of Curators',
          position: 'center center'
        },
        {
          photo: 'vicente_dolz.jpg',
          fullName: 'Vicente Dolz',
          title: 'Head of Editors',
          position: 'center center'
        },
        {
          photo: 'zenno_neagu.jpg',
          fullName: 'Zenno Neagu',
          title: 'Web Developer',
          position: 'top center'
        },
        {
          photo: 'oleg_krymskyi.jpg',
          fullName: 'Oleg Krymskyi',
          title: 'Web Developer',
          position: 'top center'
        },
        {
          photo: 'anna_pavlova.jpg',
          fullName: 'Anna Pavlova',
          title: 'UX Designer',
          position: 'center center'
        },
        {
          photo: 'nicoleta_blasek.jpg',
          fullName: 'Nicoleta Blasek',
          title: 'Social Media Manager',
          position: 'center center'
        },
        {
          photo: 'luciana_damasceno.jpg',
          fullName: 'Luciana Damasceno',
          title: 'Copywriter',
          position: 'top center'
        },
        {
          photo: 'craig_hull.jpg',
          fullName: 'Craig Hull',
          title: 'Blogger',
          position: 'center center'
        }
      ]
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'About Us | Photography Community',
      description: 'Find out why 100ASA is the most thriving community of photographers on the web. Who we are. Our story. Our team.'
    })
  }
}
</script>

<style scoped>
.bg-hero {
  background: url('/images/about-us/crew-hero.jpeg') 50% 50% no-repeat
    transparent;
  background-size: cover;
}
.overlay {
  background-color: rgba(0, 0, 0, 0.35);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.photographer-credit {
  position: absolute;
  bottom: 12px;
  right: 24px;
}
</style>
